import { Box, Button, Collapse, Flex, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useState } from 'react'

import type {
  CustomerDataApiDeployment,
  DeploymentStatus,
} from '../../../services/customerDataApiService'

export const statusColor: Record<DeploymentStatus, string> = {
  PENDING: 'blue',
  IN_PROGRESS: 'blue',
  SUCCESS: 'green',
  FAILED: 'red',
  CANCELED: 'gray',
}

export const isTerminalStatus = (s: DeploymentStatus): boolean =>
  s === 'SUCCESS' || s === 'FAILED' || s === 'CANCELED'

export const getDeploymentOutcomeLabel = (
  deployment: CustomerDataApiDeployment,
) => {
  const logs = deployment.logs ?? ''
  if (deployment.status === 'CANCELED') {
    if (/superseded by newer deployment/i.test(logs)) return 'SUPERSEDED'
    return 'CANCELED'
  }
  if (deployment.status === 'FAILED') {
    if (/Failed to notify db-subgraphs/i.test(logs)) return 'FAILED TO START'
    if (/\[WATCHDOG\]/i.test(logs)) return 'TIMED OUT'
    return 'FAILED'
  }
  return deployment.status
}

export const getOutcomeSummary = (deployment: CustomerDataApiDeployment) => {
  const logs = deployment.logs ?? ''
  if (deployment.status === 'CANCELED') {
    if (/superseded by newer deployment/i.test(logs)) {
      return 'This run was superseded by a newer pipeline run before promotion. The previously live runtime remained active.'
    }
    return 'This run was canceled before promotion. The previously live runtime remained active.'
  }
  if (deployment.status === 'FAILED') {
    if (/Failed to notify db-subgraphs/i.test(logs)) {
      return 'The API created the pipeline run, but db-subgraphs never started it.'
    }
    if (/\[WATCHDOG\]/i.test(logs)) {
      return 'The watchdog marked this run failed because progress stopped for too long.'
    }
    return 'The pipeline started but failed during setup or promotion.'
  }
  if (deployment.status === 'SUCCESS') {
    return deployment.isActive
      ? 'This successful run currently defines the live runtime.'
      : 'This run succeeded, but a newer successful config version is now live.'
  }
  if (deployment.status === 'IN_PROGRESS') {
    return 'The pipeline is actively preparing or promoting a new runtime.'
  }
  return 'The pipeline is queued and waiting to start.'
}

export type LogStep = {
  firstLine: string
  lines: string[]
  timestamp: string | null
  isError: boolean
}

/**
 * Splits raw append-only log into collapsible steps. A "step" starts at every
 * line that begins with [FAILED], [CANCELED], [WATCHDOG], or an emoji-prefixed
 * milestone ("🟢", "✅", "🔄", "🔒", etc.). This keeps the long list of
 * progress messages digestible without a log-schema change.
 */
export const parseLogIntoSteps = (logs: string): LogStep[] => {
  if (!logs.trim()) return []
  const lines = logs.split(/\r?\n/)
  const steps: LogStep[] = []
  const stepStartRe =
    /^(\[FAILED\]|\[CANCELED\]|\[WATCHDOG\]|Superseded|Deployment lock|Starting|Preparing|Setting up|Creating|Switching|All subgraphs|Pipeline|Stopping|Starting new app|Non-active|Deployment complete|Prepared|Subgraph setup complete|Prisma clients created|Clearing|Preserving|Archiving|Waiting|Pinned|Cold start|Resolving SMRT|Checking Cube|Connecting customer|Generated Prisma|Subgraph)/i
  const tsRe = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[^\s]*)/
  let current: LogStep | null = null
  for (const line of lines) {
    const isStart = stepStartRe.test(line.trimStart())
    const isError = /error|failed|\[failed\]|\[canceled\]/i.test(line)
    if (isStart) {
      if (current) steps.push(current)
      current = {
        firstLine: line,
        lines: [line],
        timestamp: tsRe.exec(line)?.[1] ?? null,
        isError,
      }
    } else if (current) {
      current.lines.push(line)
      if (isError) current.isError = true
    } else {
      current = {
        firstLine: line || '(start)',
        lines: [line],
        timestamp: tsRe.exec(line)?.[1] ?? null,
        isError,
      }
    }
  }
  if (current) steps.push(current)
  return steps
}

interface MetaCellProps {
  label: string
  children: ReactNode
}

export const MetaCell = ({ label, children }: MetaCellProps) => (
  <Flex direction="column" gap={0}>
    <Text fontSize="xs" color="presets.neutral.70">
      {label}
    </Text>
    <Text fontSize="sm">{children}</Text>
  </Flex>
)

interface LogStepRowProps {
  step: LogStep
}

export const LogStepRow = ({ step }: LogStepRowProps) => {
  const [open, setOpen] = useState(step.isError)
  const headerColor = step.isError ? 'presets.pink.70' : 'presets.neutral.90'
  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor={step.isError ? 'presets.pink.30' : 'presets.neutral.20'}
      bg={step.isError ? 'presets.pink.5' : 'presets.neutral.5'}
    >
      <Flex
        onClick={() => setOpen(o => !o)}
        px={2}
        py={1}
        align="center"
        justify="space-between"
        cursor="pointer"
      >
        <Flex gap={2} align="center" minW="0">
          <Text fontSize="xs" color="presets.neutral.70">
            {open ? '▾' : '▸'}
          </Text>
          {step.timestamp && (
            <Text fontSize="xs" color="presets.neutral.70" fontFamily="mono">
              {step.timestamp}
            </Text>
          )}
          <Text
            fontSize="xs"
            fontFamily="mono"
            color={headerColor}
            noOfLines={1}
          >
            {step.firstLine || '(empty)'}
          </Text>
        </Flex>
        <Text fontSize="10px" color="presets.neutral.70">
          {step.lines.length} line{step.lines.length === 1 ? '' : 's'}
        </Text>
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box
          as="pre"
          fontFamily="mono"
          fontSize="xs"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          p={2}
          bg="presets.neutral.10"
          borderTopWidth="1px"
          borderColor="presets.neutral.20"
        >
          {step.lines.join('\n')}
        </Box>
      </Collapse>
    </Box>
  )
}
